import { gql, useQuery } from '@apollo/client';
import { useMemo } from 'react';
import { useCurrentUser } from '@/contexts/CurrentUserContext';
import type { Query } from '@/types/GraphqlSchema';

const GET_FEATURE_FLAGS_QUERY = gql`
  {
    featureFlags
  }
`;

const FEATURE_FLAGS = {
  IN_DEVELOPMENT: 'inDevelopment',
  SCREENSHOT_MODE: 'screenshotMode',
  DISABLE_CCF_LIMIT: 'disableCcfLimit',
  MAPBOX_POC: 'mapboxPoc',
  GEOLOGY_FILTER: 'geologyFilter',
  GHG_SCOPE_3: 'ghgScope3',
  PDF_EMBEDDING: 'pdfEmbedding',
  SMART_REPORTS: 'smartReports',
  ENABLE_ASSET_REPORT_VIEW_MODE: 'enableAssetReportViewMode',
  GLOBAL_DOWNLOADS: 'globalDownloads',
  ENABLE_NEXUS_MENU: 'enableNexusMenu',
  ENABLE_CONSOLIDATED_LIVE_MODELS: 'enableConsolidatedLiveModels',
  SEARCH_ASSISTANT: 'searchAssistant',
  CONSOLIDATED_LIVE_MODELS_ADMIN: 'consolidatedLiveModelsAdmin',
  VALUATIONS_V2: 'valuationsV2',
  PORTFOLIO_ANALYSIS_WELL_COUNTS: 'portfolioAnalysisWellCounts',
  PORTFOLIO_BRIDGE: 'portfolioBridge',
  LNG_ASSET_VALUATION: 'lngAssetValuation',
  PORTFOLIO_ENTITLEMENT: 'portfolioEntitlement',
} as const;

type FeatureFlagKey = (typeof FEATURE_FLAGS)[keyof typeof FEATURE_FLAGS];

type FeatureFlagsState = {
  [K in FeatureFlagKey]: boolean;
} & {
  flagsLoaded: boolean;
  enableAssetReportPdfViewMode: boolean;
};

const THIRTY_MINUTES = 1000 * 60 * 30;

const useGetFeatureFlags = () => {
  const { isAuthenticated } = useCurrentUser();

  const { data, loading } = useQuery<Pick<Query, 'featureFlags'>>(GET_FEATURE_FLAGS_QUERY, {
    skip: !isAuthenticated,
    nextFetchPolicy: 'cache-and-network',
    pollInterval: THIRTY_MINUTES,
  });

  const featureFlags = data?.featureFlags ?? null;

  return useMemo(() => {
    const isFlagEnabled = (flagKey: FeatureFlagKey) => featureFlags?.[flagKey] ?? false;

    const flagsState: Partial<FeatureFlagsState> = {
      flagsLoaded: !loading && featureFlags !== null,
    };

    for (const flagKey of Object.values(FEATURE_FLAGS)) {
      flagsState[flagKey] = isFlagEnabled(flagKey);
    }

    flagsState.enableAssetReportPdfViewMode = !!flagsState.enableAssetReportViewMode;

    return flagsState;
  }, [loading, featureFlags]);
};

export { useGetFeatureFlags, FEATURE_FLAGS, type FeatureFlagsState };
